import React, { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  ColumnFilterOption,
  ColumnSizing,
} from 'components/ReorderResizeTable/types';
import {
  COLUMNS,
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  SellersTableData,
  SellersTableColumns,
  BODY_CONTENT_GENERATE,
} from './data';
import SellerDrawer from '../SellerDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import useDrawerNavigation from 'hooks/useDrawerNavigation';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';

type SellersTableProps = {
  className?: string;
  isLoading?: boolean;
  sort?: string;
  totalPages?: number;
  totalItems?: number;
  data: SellersTableData[];
  columns?: SellersTableColumns;
  columnFilterOptions?: Record<string, ColumnFilterOption>;
  columnSizing?: ColumnSizing;
  onColumnSizingChange?: (columnSizing: ColumnSizing) => void;
  onReorder?: (columns: SellersTableColumns) => void;
};

const SellersTable: FC<SellersTableProps> = (props) => {
  const {
    className,
    isLoading,
    sort,
    totalPages = 1,
    totalItems,
    data,
    columns = COLUMNS,
    columnFilterOptions,
    columnSizing,
    onColumnSizingChange,
    onReorder,
  } = props;

  const [selectedSeller, setSelectedSeller] = useState<SellersTableData>();

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>();

  const classes = classNames('sellers-table', className);

  const openSeller = useCallback(
    (seller: SellersTableData) => {
      setSelectedSeller(seller);
      drawer?.setOpen(true);
    },
    [drawer],
  );

  const { onPrev, onNext } = useDrawerNavigation({
    rows: data,
    selectedId: selectedSeller?._id,
    totalPages,
    isLoading,
    onNavigate: openSeller,
  });

  const tableContentTransformations = useMemo(
    () =>
      COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (data: SellersTableData) =>
            BODY_CONTENT_GENERATE[col]({
              data,
            }),
        }),
        {},
      ),
    [],
  );

  return (
    <>
      <ReorderResizeTable
        className={classes}
        sort={sort}
        totalPages={totalPages}
        totalItems={totalItems}
        recordName="sellerRecord"
        data={data}
        columns={columns}
        headingTranslations={HEADING_TRANSLATIONS}
        sortingValues={SORTING_VALUES}
        isDataLoading={isLoading}
        tableContentTransformations={tableContentTransformations}
        customSizes={CUSTOM_COL_SIZES}
        columnFilterOptions={columnFilterOptions}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder}
        onRowClick={openSeller}
      />

      <SellerDrawer
        ref={drawerRef}
        seller={selectedSeller}
        onPrev={onPrev}
        onNext={onNext}
      />
    </>
  );
};

export default SellersTable;
